"use client"

import { BillingIntervalToggle } from './BillingIntervalToggle';

import { motion } from "framer-motion"
import { AlertTriangle, ArrowLeft, Check, Sparkles, X } from "lucide-react"
import { geist } from "@/lib/fonts"
import { useAuth } from "@/contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog'
import { Button } from './ui/button'

// Static array of pricing plans
// Include Stripe priceId for paid plans
const pricingPlans = [
	{
		id: '6d4ecf79-200c-4efc-85a9-e1205386d362',
		name: "Free Plan",
		priceLabel: "0$ / forever",
		features: [
			"Capture up to 200 fans (emails/texts/socials)",
			"1 campaign per month (blast your fans with a track, merch drop, or show)",
			"Basic fan analytics (total fans, engagement rate)",
			"1 smart link / QR code (to promote your music)",
		],
		cta: "Join for Free",
		link: "/settings",
	},
 	{
 		id: '6b8ff00f-be5d-4da4-b007-06b4e27a2226',
 		name: "Growth Plan",
 		priceLabel: "$19.00 / month",
		// Static fallback pricing data (monthly & yearly) used if DB fetch fails
		price_cents: 1900,
		monthly: { id: '6b8ff00f-be5d-4da4-b007-06b4e27a2226', interval: 'month', price_cents: 1900, stripe_price_id: 'price_1S77FM4H36Uva1e4k8rSlQZI' },
		yearly: { id: '3775e19a-4d33-4d6d-b979-30a3e47ba152', interval: 'year', price_cents: 12000, stripe_price_id: 'price_1S77Ft4H36Uva1e4q1Id8R5d' },
		features: [
			"Everything in Free, plus:",
			"Capture up to 2,500 fans.",
			"Unlimited campaigns (email + SMS).",
			"Advanced fan analytics (top listeners, engagement by release, fan location).",
			"Custom fan tags & segmentation (separate casual listeners vs superfans).",
			"3 smart links / QR codes (multi-project promos).",
			"Priority email support.",
		],
		cta: "Join Growth Plan",
		priceId: 'price_1S77FM4H36Uva1e4k8rSlQZI',
		popular: true,
	},
 		{
 		id: '2aadfdd1-ec49-4e7f-a1ce-c3801fa0180e',
 		name: "Pro Plan",
 		priceLabel: "$49.00 / month",
		price_cents: 4900,
		monthly: { id: '2aadfdd1-ec49-4e7f-a1ce-c3801fa0180e', interval: 'month', price_cents: 4900, stripe_price_id: 'price_1S77Gt4H36Uva1e4d6hMADiw' },
		yearly: { id: '650b948a-00b9-4218-83e3-27afd6208f0c', interval: 'year', price_cents: 35000, stripe_price_id: 'price_1S77HR4H36Uva1e4tlksZpHG' },
			features: [
			"Everything in Growth, plus:",
			"Capture up to 10,000 fans.",
			"Automated fan journeys (welcome flows, follow-ups after releases, birthday/ anniversary messages).",
			"Merch + ticketing integrations (sell directly to your fans, track buyer, implemented later).",
			"Collaboration tools (cross-promos with other artists).",
			"Up to 10 smart links / QR codes.",
			"1-on-1 onboarding call.",
			],
			cta: "Join Pro Plan",
			priceId: 'price_1S77Gt4H36Uva1e4d6hMADiw',
 		},
]

export function PricingSection({ plans = pricingPlans, onSubscribe }) {
    const [showDowngradeDialog, setShowDowngradeDialog] = useState(false)
    const [pendingPlan, setPendingPlan] = useState(null)
	const FREE_PLAN_ID = '6d4ecf79-200c-4efc-85a9-e1205386d362'
	const { user } = useAuth()
	const navigate = useNavigate()
	const [ownedPlanId, setOwnedPlanId] = useState(FREE_PLAN_ID)
	const [credits, setCredits] = useState(0)
	const [discountStatus, setDiscountStatus] = useState({ hasDiscount: false, percentOff: null })
	const [inviteeUnused, setInviteeUnused] = useState(false)
	const [billingInterval, setBillingInterval] = useState('month') // 'month' | 'year'

	// Fetch subscription: if none, user owns free plan; else set to returned plan_id
	useEffect(() => {
		if (!user) { setOwnedPlanId(FREE_PLAN_ID); return }
		let cancelled = false
		supabase
			.from('subscriptions')
			.select('plan_id')
			.eq('user_id', user.id)
			.maybeSingle()
			.then(({ data, error }) => {
				if (cancelled) return
				if (error) {
					console.error('Load subscription error:', error)
					setOwnedPlanId(FREE_PLAN_ID)
					return
				}
				setOwnedPlanId(data?.plan_id || FREE_PLAN_ID)
			})
		return () => { cancelled = true }
	}, [user])
	// Build displayPlans merging dynamic API data with static fallbacks (monthly/yearly)
	const displayPlans = pricingPlans.map(staticDef => {
		if (staticDef.link) return staticDef
		const dynMonthly = (plans || []).find(p => p.name === staticDef.name && p.interval === 'month') || {}
		const dynYearly = (plans || []).find(p => p.name === staticDef.name && p.interval === 'year') || {}
		const monthly = dynMonthly.id ? dynMonthly : (staticDef.monthly || {})
		const yearly = dynYearly.id ? dynYearly : (staticDef.yearly || {})
		const active = billingInterval === 'year' && yearly.id ? yearly : (monthly.id ? monthly : yearly)
		return {
			...staticDef,
			monthly: monthly.id ? monthly : null,
			yearly: yearly.id ? yearly : null,
			interval: active.interval || 'month',
			price_cents: active.price_cents ?? staticDef.price_cents ?? null,
			priceId: active.stripe_price_id || staticDef.priceId,
			id: active.id || staticDef.id,
		}
	})

	// Load referral credits to show discount messaging
	useEffect(() => {
		let cancelled = false
		;(async () => {
			if (!user) { setCredits(0); return }
			try {
				const res = await fetch(`https://backend.amply.buildfast.us/referral/credits/${user.id}`)
				if (!res.ok) throw new Error(`credits http ${res.status}`)
				const json = await res.json()
				if (!cancelled) setCredits(json?.credits || 0)
			} catch (e) {
				console.warn('Could not load referral credits:', e)
				if (!cancelled) setCredits(0)
			}
		})()
		return () => { cancelled = true }
	}, [user?.id])

	// Check if current user is an invitee with an unused 50% referral discount
	useEffect(() => {
		let cancelled = false
		;(async () => {
			if (!user) { setInviteeUnused(false); return }
			try {
				const { data, error } = await supabase
					.from('referrals')
					.select('id, activated_at, created_at')
					.eq('invitee_id', user.id)
					.order('created_at', { ascending: false })
					.limit(1)
					.maybeSingle()
				if (error) {
					console.warn('Load invitee referral status error:', error)
					if (!cancelled) setInviteeUnused(false)
					return
				}
				// Show invitee discount when activated_at is null (not activated yet)
				if (!cancelled) setInviteeUnused(!!(data && data.activated_at == null))
			} catch (e) {
				if (!cancelled) setInviteeUnused(false)
			}
		})()
		return () => { cancelled = true }
	}, [user?.id])

	// Load live discount status for the current subscription to reflect active Stripe discounts
	useEffect(() => {
		let cancelled = false
		;(async () => {
			if (!user) { setDiscountStatus({ hasDiscount: false, percentOff: null }); return }
			try {
					const r = await fetch(`https://backend.amply.buildfast.us/subscription/discount-status/${encodeURIComponent(user.id)}`)
				const j = await r.json()
				if (!cancelled) setDiscountStatus({ hasDiscount: !!j?.hasDiscount, percentOff: j?.percentOff ?? null })
			} catch (e) {
				if (!cancelled) setDiscountStatus({ hasDiscount: false, percentOff: null })
			}
		})()
		return () => { cancelled = true }
	}, [user?.id])

	const computePriceView = (plan) => {
		const cents = plan?.price_cents
		if (cents == null) return { label: plan.priceLabel, original: null, discounted: null, note: null, savings: null, perMonth: null }
		const base = (cents / 100).toFixed(2)
		const periodLabel = plan.interval === 'year' ? 'year' : 'month'
		const hasActiveDiscountOnOwnedPlan = (plan?.id === ownedPlanId) && discountStatus.hasDiscount && cents > 0
		const hasInviteeDiscount = !hasActiveDiscountOnOwnedPlan && inviteeUnused && cents > 0
		const hasCredit = !hasActiveDiscountOnOwnedPlan && !hasInviteeDiscount && credits > 0 && cents > 0
		const percentOff = (typeof discountStatus.percentOff === 'number') ? discountStatus.percentOff : 50
		const discounted = (hasActiveDiscountOnOwnedPlan)
			? (Math.max(0, cents * (1 - percentOff / 100)) / 100).toFixed(2)
			: ((hasInviteeDiscount || hasCredit) ? (Math.max(0, cents * 0.5) / 100).toFixed(2) : null)
		// savings if yearly available
		let savings = null; let perMonth = null
		if (plan.interval === 'year' && plan.monthly?.price_cents && plan.yearly?.price_cents) {
			const annualized = plan.monthly.price_cents * 12
			const diff = annualized - plan.yearly.price_cents
			if (diff > 0) {
				const pct = Math.round((diff / annualized) * 100)
				savings = pct
				perMonth = (plan.yearly.price_cents / 12 / 100).toFixed(2)
			}
		}
		return {
			label: `$${base} / ${periodLabel}`,
			original: (hasActiveDiscountOnOwnedPlan || hasInviteeDiscount || hasCredit) ? `$${base}` : null,
			discounted: (hasActiveDiscountOnOwnedPlan || hasInviteeDiscount || hasCredit) ? `$${discounted} / ${periodLabel}` : null,
			note: hasActiveDiscountOnOwnedPlan ? `Current ${periodLabel}` : ((hasInviteeDiscount || hasCredit) ? (plan.interval === 'year' ? 'first year' : 'first month') : null),
			savings,
			perMonth,
		}
	}

	// Yearly referral note helper
	const renderReferralYearlyNote = (plan) => {
		if (!plan || plan.link) return null
		const isYear = billingInterval === 'year' && plan.interval === 'year'
		if (!isYear) return null
		// One-time referral coupon applies to FIRST billing period only.
		// For yearly plans that means first YEAR at 50% off; renewals revert to standard price unless
		// another discount/credit is applied manually before renewal.
		if (inviteeUnused || (discountStatus.hasDiscount && ownedPlanId === plan.id)) {
			return <p className="mt-1 text-xs text-amber-300">50% off first year applies once</p>
		}
		return null
	}

    const defaultSubscribe = async (plan) => {
        // Free plan or link
        if (plan.link) {
            const { error: deleteErr } = await supabase
      .from('subscriptions')
      .delete()
      .eq('user_id', user.id)
    if (deleteErr) {
      console.error('Failed to delete previous subscriptions:', deleteErr)
    }

	
	 const { data: subData, error: insertErr } = await supabase
      .from('subscriptions')
      .insert({ user_id: user.id, plan_id: FREE_PLAN_ID, status: 'active' })
      .select('id')
      .single()
    const subErr = insertErr
    if (subErr) {
      console.error('Subscription insert error:', subErr)
    } else {
      console.log('Inserted new subscription id:', subData.id)
    }
            navigate(plan.link)
            return
        }
        if (!user) {
            navigate('/login')
            return
        }
	// Create Checkout session
	const traceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
		const res = await fetch('https://backend.amply.buildfast.us/create-checkout-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
	    body: JSON.stringify({ priceId: plan.priceId, userId: user.id, ref: localStorage.getItem('referrerId') || '', traceId }),
        })
        const data = await res.json()
        if (data.url) window.location.href = data.url
        else alert(data.error || 'Unable to start checkout')
    }
    // Use provided onSubscribe or default
    const subscribeFn = onSubscribe || defaultSubscribe

    return (
        <>
            <Dialog open={showDowngradeDialog} onOpenChange={setShowDowngradeDialog}>
      <DialogContent className="sm:max-w-md bg-white border-border shadow-2xl">
        <button
          onClick={() => setShowDowngradeDialog(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader className="space-y-4 text-center sm:text-left">
          <div style={{backgroundColor: '#fce4e4'}} className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 sm:mx-0">
            <AlertTriangle style={{color: 'red'}} className="h-6 w-6 text-destructive" />
          </div>

          <div className="space-y-2">
            <DialogTitle className="text-xl font-semibold text-foreground">Downgrade to Free Plan?</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              Downgrading to the free plan will cancel your current paid subscription and you will lose access to paid
              features. This action cannot be undone.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="rounded-lg bg-muted/50 p-4 space-y-2">
          <h4 className="text-sm font-medium text-foreground">You will lose access to:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li className="flex items-center gap-2">
              <div style={{backgroundColor: 'red'}} className="h-1.5 w-1.5 rounded-full bg-destructive" />
              Premium features and tools
            </li>
            <li className="flex items-center gap-2">
              <div style={{backgroundColor: 'red'}} className="h-1.5 w-1.5 rounded-full bg-destructive" />
              Priority customer support
            </li>
            <li className="flex items-center gap-2">
              <div style={{backgroundColor: 'red'}} className="h-1.5 w-1.5 rounded-full bg-destructive" />
              Advanced analytics and insights
            </li>
          </ul>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setShowDowngradeDialog(false)} className="w-full sm:w-auto">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Keep Current Plan
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setShowDowngradeDialog(false)
              if (pendingPlan) subscribeFn(pendingPlan)
            }}
		style={{backgroundColor: 'red', color: 'white'}}
            className="w-full sm:w-auto font-medium"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Yes, Downgrade Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
            <section className="relative py-24 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
						className="text-center mb-16"
					>
						<motion.div
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.5 }}
							className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
						>
							<Sparkles className="w-4 h-4 text-[#3c5b3]" />
							<span className="text-sm font-medium text-white/80">Pricing</span>
						</motion.div>

						<h2
							className={["mb-8 text-center text-4xl font-semibold tracking-tighter text-white md:text-[54px] md:leading-[60px] relative z-10", geist.className].filter(Boolean).join(" ")}
							style={{
								textShadow: '0 0 16px #e3c5b3, 0 0 4px #fff',
							}}
						>
							Choose your plan
						</h2>

						<p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">
							Start growing your audience today. Choose Amply to manage your fans
							and boost your music career.
						</p>
					</motion.div>
							<div className="flex justify-center mt-10 mb-12">
											<BillingIntervalToggle value={billingInterval} onChange={setBillingInterval} />
										</div>
					{/* Pricing Cards */}
					<div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
			
						{displayPlans.map((plan, index) => (
							<motion.div
								key={plan.name}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.6, delay: index * 0.1 }}
								whileHover={{ y: -5 }}
								className={`relative rounded-2xl p-8 backdrop-blur-sm border transition-all duration-300 ${
									(plan === displayPlans[1])
										? "bg-gradient-to-b from-[#e3c5b3]/10 to-transparent border-[#e3c5b3]/30 shadow-lg shadow-[#e3c5b3]/10"
										: "bg-white/5 border-white/10 hover:border-white/20"
								}`}
							>
								{plan === displayPlans[1] && (
									<div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
										<div className="bg-gradient-to-r from-[#e3c5b3] to-[#e3c5b3]/80 text-white text-sm font-medium px-4 py-2 rounded-full">
											Most Popular
										</div>
									</div>
								)}

								<div className="text-center mb-8">
									<h3 className="text-xl font-bold text-white mb-2">
										{plan.name}
									</h3>
									{/* Invitee referral badge */}
									{inviteeUnused && plan?.price_cents > 0 && (
										<div className="mb-2">
											<span className="text-xs px-2 py-1 rounded-full bg-white/10 border border-white/20 text-white">Referral 50% off available</span>
										</div>
									)}
									<div className="flex items-baseline justify-center gap-3 mb-2 flex-wrap">
										{(() => { const pv = computePriceView(plan); return pv.original && pv.discounted ? (
											<>
												<span className="text-2xl line-through opacity-70">{pv.original}</span>
												<span className="text-4xl font-bold text-white">{pv.discounted}</span>
												<span className="text-white/70 text-sm">{pv.note}</span>
											</>
										) : (
											<span className="text-4xl font-bold text-white">{pv.label}</span>
										)})()}
									</div>
									{(() => { const pv = computePriceView(plan); return (
										<div className="text-sm text-white/60 space-x-2">
											{pv.perMonth && <span>≈ ${pv.perMonth}/mo</span>}
											{pv.savings && <span className="text-green-400">Save {pv.savings}%</span>}
										</div>
									)})()}
									{renderReferralYearlyNote(plan)}
								</div>

								<ul className="space-y-4 mb-8">
									{(plan.features || []).map((feature, featureIndex) => (
										<li key={featureIndex} className="flex items-center gap-3">
											<Check className="w-5 h-5 text-[#e3c5b3] flex-shrink-0" />
											<span className="text-white/80 text-sm">{feature}</span>
										</li>
									))}
								</ul>

                                <div className="flex justify-center">
				    <Button
                                        onClick={() => {
                                            if (user && plan.id === FREE_PLAN_ID && ownedPlanId !== FREE_PLAN_ID) {
                                                setPendingPlan(plan)
                                                setShowDowngradeDialog(true)
                                            } else if (!(user && plan.id === ownedPlanId)) {
                                                subscribeFn(plan)
                                            }
                                        }}
                                        disabled={user && plan.id === ownedPlanId}
                                        className={`w-full max-w-xs px-6 py-3 rounded-full text-sm text-center font-medium text-white transition ${
                                            (user && plan.id === ownedPlanId)
                                                ? 'bg-white/20 border border-white/20 cursor-default'
                                                : plan.link === '/register'
                                                    ? 'bg-transparent border border-white/20 hover:bg-white/10'
                                                    : 'bg-white/10 border border-white/20 hover:bg-white/20'
                                        }`}
                                    >
										{user && plan.id === ownedPlanId
											? (discountStatus.hasDiscount ? 'Discount active this period' : 'You already own this plan')
											: ((inviteeUnused || credits > 0) && plan?.price_cents > 0 ? 'Get 50% Off First Month' : plan.cta)}
                                    </Button>
                                </div>
							</motion.div>
						))}
					</div>



	
				</div>
			</section>
        </>
    )
}
